import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  apiErrorStatus,
  apiErrorText,
  useCreateGroupRateMutation,
  useSuggestGroupRateQuery,
  type GroupRate,
  type GroupRateBasis,
  type GroupRatePick,
  type GroupRateSource,
  type NewGroupRate,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { DrawerBody, DrawerFooter } from '@shared/ui/Drawer';
import {
  Actions,
  BASIS_HINT,
  BASIS_LABEL,
  BlockHeading,
  BlockSection,
  ChoiceCard,
  ChoiceFieldset,
  ChoiceGrid,
  Control,
  ControlArea,
  FieldError,
  FieldHint,
  FieldLabel,
  FooterStatus,
  Notice,
  TextButton,
  addDaysIso,
  defaultValidFrom,
  fleetType,
  formatIsoDate,
  formatMoney,
  isIsoDate,
  parseRateInput,
  rateInputsInfo,
  rateUnit,
  suggestionModelIds,
  typo,
  validFromIssue,
  type RateRow,
} from '@entities/fleet';
import { SuggestionPanel } from './SuggestionPanel';
import { targetLabel, type RateTarget } from './scope';

const BASES: readonly GroupRateBasis[] = ['cost', 'prc'];

/** Текст сервера — законченным предложением: после него идёт наше пояснение. */
function sentence(text: string): string {
  const t = text.trim();
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

const Narrow = styled.div`
  max-width: ${({ theme }) => theme.spacing(80)};
`;

const DateControl = styled(Control)`
  font-variant-numeric: tabular-nums;
`;

const RateControl = styled(Control)`
  font-size: ${fleetType.lead};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

const Stack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Notices = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};

  &:empty {
    display: none;
  }
`;

interface RateFormProps {
  target: RateTarget;
  row: RateRow;
  /** Дата экрана — «действует с» по умолчанию. */
  on: string;
  /** Последняя ставка группы у этого объекта (или по умолчанию) — её закроет новая. */
  open: GroupRate | null;
  formId: string;
  /** Ссылка на группу в справочнике — добавить модели. */
  groupHref: string;
  onCancel: () => void;
  onSaved: (rate: GroupRate) => void;
  /** Перечитать историю и ставки после 409. */
  onRefresh: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

/**
 * Новая ставка группы: «действует с», база (по умолчанию себестоимость) и
 * основание — подсказка системы (среднее или средневзвешенное по отмеченным
 * моделям) или ввод вручную; комментарий. Прежняя ставка закроется днём
 * раньше; задним числом ставку не меняют.
 */
export function RateForm({
  target,
  row,
  on,
  open,
  formId,
  groupHref,
  onCancel,
  onSaved,
  onRefresh,
  onDirtyChange,
}: RateFormProps) {
  const [initialDate] = useState(() => defaultValidFrom(on, open));
  const [validFrom, setValidFrom] = useState(initialDate);
  const [basis, setBasis] = useState<GroupRateBasis>('cost');
  const [sourceChoice, setSourceChoice] = useState<GroupRateSource | null>(null);
  const [pick, setPick] = useState<GroupRatePick>('average');
  const [rateInput, setRateInput] = useState('');
  const [comment, setComment] = useState('');
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [checkedTouched, setCheckedTouched] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [createRate, { isLoading: saving }] = useCreateGroupRateMutation();

  const dateOk = isIsoDate(validFrom);
  // Подсказка — по ПРЦ объекта ставки (своя ПРЦ объекта или общая).
  const projectId = target.kind === 'project' ? target.projectId : undefined;
  const fullQ = useSuggestGroupRateQuery(
    { group_id: row.groupId, date: validFrom, basis, project_id: projectId },
    { skip: !dateOk },
  );
  const full = dateOk ? fullQ.data : undefined;
  const all = useMemo(() => full?.models.map((m) => m.model_id) ?? [], [full]);
  const subsetIds = full && checked ? suggestionModelIds(all, checked) : undefined;
  const subsetQ = useSuggestGroupRateQuery(
    {
      group_id: row.groupId,
      date: validFrom,
      basis,
      model_ids: subsetIds ?? [],
      project_id: projectId,
    },
    { skip: !dateOk || !subsetIds || subsetIds.length === 0 },
  );
  const active = subsetIds ? (subsetIds.length > 0 ? subsetQ.data : undefined) : full;

  // Прежняя ставка по подсказке считалась по части моделей — отмечаем их же.
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current || checkedTouched || !full) return;
    prefillDone.current = true;
    if (!open || open.source !== 'suggestion') return;
    const info = rateInputsInfo(open.inputs);
    if (!info) return;
    if (info.pick) setPick(info.pick);
    const ids = new Set(info.modelIds.filter((id) => all.includes(id)));
    if (ids.size > 0 && ids.size < all.length) {
      setChecked(ids);
      setPrefilled(true);
    }
  }, [full, all, open, checkedTouched]);

  // Можно ли взять подсказку ставкой — решает сервер (`applicable`: у группы
  // «в сутках» нельзя, ПРЦ — ₽ за маш.-час). Пока подсказка грузится — по
  // единице учёта группы, чтобы переключатель не мигал.
  const notApplicable = full ? !full.applicable : row.unit === 'day';
  const noPrc = full !== undefined && full.models_count === 0;
  // Подсказку взять нельзя — только вручную, что бы ни выбрали раньше.
  const forcedManual = notApplicable || noPrc;
  const source: GroupRateSource = forcedManual ? 'manual' : (sourceChoice ?? 'suggestion');

  const dirty =
    validFrom !== initialDate ||
    basis !== 'cost' ||
    sourceChoice !== null ||
    rateInput !== '' ||
    comment !== '' ||
    checkedTouched;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const dateIssue = validFromIssue(validFrom, open);
  const manual = source === 'manual' ? parseRateInput(rateInput) : null;
  const manualIssue = manual && 'error' in manual ? manual.error : null;
  const pickedValue = source === 'suggestion' && active ? active[pick] : null;
  const suggestionIssue =
    source !== 'suggestion'
      ? null
      : subsetIds && subsetIds.length === 0
        ? 'Отметьте хотя бы одну модель'
        : !active
          ? 'Подсказка ещё считается — подождите секунду'
          : pickedValue === null
            ? 'В подсказке нет значения — введите ставку вручную'
            : null;

  const noticesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (serverError) noticesRef.current?.scrollIntoView({ block: 'start' });
  }, [serverError]);

  const id = (key: string) => `${formId}-${key}`;

  function focus(key: string) {
    const el = document.getElementById(id(key));
    el?.scrollIntoView({ block: 'center' });
    el?.focus({ preventScroll: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (dateIssue || manualIssue || suggestionIssue) {
      setShowErrors(true);
      focus(dateIssue ? 'valid-from' : manualIssue ? 'rate' : 'suggestion');
      return;
    }
    const base = {
      ...(target.kind === 'project' ? { project_id: target.projectId } : {}),
      group_id: row.groupId,
      valid_from: validFrom,
      basis,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    };
    let body: NewGroupRate;
    if (source === 'manual') {
      if (!manual || 'error' in manual) return;
      body = { ...base, source: 'manual', rate: manual.value };
    } else {
      body = {
        ...base,
        source: 'suggestion',
        pick,
        ...(subsetIds ? { model_ids: subsetIds } : {}),
      };
    }
    try {
      const res = await createRate(body).unwrap();
      onSaved(res.rate);
    } catch (err) {
      const conflict = apiErrorStatus(err) === 409;
      setServerError({
        text: sentence(apiErrorText(err, 'Не удалось сохранить ставку.')),
        conflict,
      });
      if (conflict) onRefresh();
    }
  }

  const unitText = rateUnit(row.unit);
  const closes = open && dateOk && !dateIssue ? addDaysIso(validFrom, -1) : null;
  const submitLabel = saving
    ? 'Сохраняю…'
    : source === 'manual'
      ? 'Сохранить ставку'
      : `Принять ${pick === 'average' ? 'среднее' : 'средневзвешенное'}${
          pickedValue ? ` — ${formatMoney(pickedValue)}` : ''
        }`;
  const firstIssue = dateIssue ?? manualIssue ?? suggestionIssue;

  return (
    <>
      <DrawerBody>
        <Notices ref={noticesRef}>
          {serverError && (
            <Notice $tone={serverError.conflict ? 'warning' : 'danger'} role="alert">
              <span className="text">
                <strong>{serverError.text}</strong>
                {serverError.conflict &&
                  ` ${typo('История ставок перечитана — проверьте дату «действует с» и сохраните ещё раз.')}`}
              </span>
              {serverError.conflict && (
                <TextButton type="button" onClick={onRefresh}>
                  Перечитать ещё раз
                </TextButton>
              )}
            </Notice>
          )}
        </Notices>

        <form id={formId} onSubmit={submit} noValidate>
          <BlockSection aria-labelledby={id('date-h')}>
            <BlockHeading id={id('date-h')}>С какой даты</BlockHeading>
            <Narrow>
              <FieldLabel htmlFor={id('valid-from')}>
                <span>
                  Действует с
                  <span className="req" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </span>
              </FieldLabel>
              <DateControl
                id={id('valid-from')}
                type="date"
                value={validFrom}
                aria-required="true"
                aria-invalid={showErrors && dateIssue ? true : undefined}
                aria-describedby={[
                  id('valid-from-hint'),
                  showErrors && dateIssue ? id('valid-from-error') : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            </Narrow>
            <FieldHint id={id('valid-from-hint')}>
              {typo(
                open
                  ? closes
                    ? `Прежняя ставка ${formatMoney(open.rate)} ${unitText} (с ${formatIsoDate(open.valid_from)}) закроется ${formatIsoDate(closes)}. Задним числом ставку не меняют.`
                    : `Последняя ставка действует с ${formatIsoDate(open.valid_from)} — новая начинается позже. Задним числом ставку не меняют.`
                  : 'Ставка действует с этой даты, пока её не сменит следующая. Задним числом её потом не поменять — только новой ставкой.',
              )}
            </FieldHint>
            {showErrors && dateIssue && (
              <FieldError id={id('valid-from-error')}>{dateIssue}</FieldError>
            )}
          </BlockSection>

          <BlockSection aria-labelledby={id('basis-h')}>
            <BlockHeading id={id('basis-h')}>База ставки</BlockHeading>
            <ChoiceFieldset>
              <legend>
                {typo(
                  'Из чего ставка: обе без НДС. По умолчанию — себестоимость, как в январском БДР.',
                )}
              </legend>
              <ChoiceGrid>
                {BASES.map((b) => (
                  <ChoiceCard key={b}>
                    <input
                      type="radio"
                      name={id('basis')}
                      value={b}
                      checked={basis === b}
                      onChange={() => setBasis(b)}
                    />
                    <span className="title">
                      {BASIS_LABEL[b]}
                      {b === 'cost' ? ' — по умолчанию' : ''}
                    </span>
                    <span className="hint">{typo(BASIS_HINT[b])}</span>
                  </ChoiceCard>
                ))}
              </ChoiceGrid>
            </ChoiceFieldset>
          </BlockSection>

          <BlockSection aria-labelledby={id('source-h')}>
            <BlockHeading id={id('source-h')}>Ставка</BlockHeading>
            <ChoiceFieldset>
              <legend>Как задать ставку</legend>
              <ChoiceGrid>
                <ChoiceCard>
                  <input
                    type="radio"
                    name={id('source')}
                    value="suggestion"
                    checked={source === 'suggestion'}
                    disabled={forcedManual}
                    onChange={() => setSourceChoice('suggestion')}
                  />
                  <span className="title">По подсказке системы</span>
                  <span className="hint">
                    {typo(
                      notApplicable
                        ? 'Недоступно: группа учитывается в сутках, а ПРЦ — цена маш.-часа.'
                        : noPrc
                          ? 'Недоступно: у моделей группы нет утверждённой ПРЦ на эту дату.'
                          : 'Среднее утверждённых ПРЦ моделей группы. Сохранится, из каких ПРЦ и версий оно получено.',
                    )}
                  </span>
                </ChoiceCard>
                <ChoiceCard>
                  <input
                    type="radio"
                    name={id('source')}
                    value="manual"
                    checked={source === 'manual'}
                    onChange={() => setSourceChoice('manual')}
                  />
                  <span className="title">Вручную</span>
                  <span className="hint">
                    {typo('Число, о котором договорились, — например, по объекту.')}
                  </span>
                </ChoiceCard>
              </ChoiceGrid>
            </ChoiceFieldset>

            <Stack>
              {source === 'manual' && (
                <div>
                  <Narrow>
                    <FieldLabel htmlFor={id('rate')}>
                      <span>
                        Ставка, {unitText} без НДС
                        <span className="req" aria-hidden="true">
                          {' '}
                          *
                        </span>
                      </span>
                    </FieldLabel>
                    <RateControl
                      id={id('rate')}
                      value={rateInput}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      spellCheck={false}
                      aria-required="true"
                      aria-invalid={showErrors && manualIssue ? true : undefined}
                      aria-describedby={
                        showErrors && manualIssue ? id('rate-error') : id('rate-hint')
                      }
                      onChange={(e) => setRateInput(e.target.value)}
                    />
                  </Narrow>
                  {showErrors && manualIssue ? (
                    <FieldError id={id('rate-error')}>{manualIssue}</FieldError>
                  ) : (
                    <FieldHint id={id('rate-hint')}>
                      Копейки — через запятую. Разряды можно разделять пробелом.
                    </FieldHint>
                  )}
                </div>
              )}
              <div id={id('suggestion')} tabIndex={-1}>
                <SuggestionPanel
                  full={full}
                  active={active}
                  loading={dateOk && fullQ.isLoading}
                  updating={subsetQ.isFetching}
                  error={
                    !dateOk
                      ? 'Укажите дату «действует с» — подсказка считается по ПРЦ, действующим на неё.'
                      : fullQ.isError
                        ? apiErrorText(fullQ.error, 'Не удалось посчитать подсказку.')
                        : null
                  }
                  picking={source === 'suggestion'}
                  pick={pick}
                  onPick={setPick}
                  checked={checked}
                  onChecked={(next) => {
                    setChecked(next);
                    setCheckedTouched(true);
                    setPrefilled(false);
                  }}
                  date={validFrom}
                  groupHref={groupHref}
                  idPrefix={formId}
                />
                {prefilled && (
                  <FieldHint>
                    {typo(
                      'Отмечены модели, по которым считалась прежняя ставка, — проверьте, те ли машины сейчас на объекте.',
                    )}
                  </FieldHint>
                )}
                {showErrors && suggestionIssue && <FieldError>{suggestionIssue}</FieldError>}
              </div>
            </Stack>
          </BlockSection>

          <BlockSection aria-labelledby={id('comment-h')}>
            <BlockHeading id={id('comment-h')}>Комментарий</BlockHeading>
            <FieldLabel htmlFor={id('comment')}>
              Почему такая ставка — увидят в истории ставок
            </FieldLabel>
            <ControlArea
              id={id('comment')}
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </BlockSection>
        </form>
      </DrawerBody>
      <DrawerFooter>
        <FooterStatus role="status">
          {showErrors && firstIssue
            ? firstIssue
            : `${targetLabel(target)} · с ${dateOk ? formatIsoDate(validFrom) : '…'}`}
        </FooterStatus>
        <Actions>
          <Button type="button" $variant="ghost" onClick={onCancel} disabled={saving}>
            Отмена
          </Button>
          <Button type="submit" form={formId} $variant="primary" disabled={saving}>
            {submitLabel}
          </Button>
        </Actions>
      </DrawerFooter>
    </>
  );
}
